import type { FixedKey, HandsetLayout } from '@arcnet/contracts';

/**
 * A desk phone, drawn from its plug-in's layout (docs/29 decision 14): the template editor's
 * main view, modelled on Yealink's Configuration Generator, where the phone is clicked key by key.
 *
 * Our own outline, never a vendor photo: it can show our labels on its screen, turn its pages,
 * follow the theme and be used from a keyboard. Keys that can be set are buttons; the keypad,
 * headset, speaker and volume are drawn for where they are and nothing more.
 *
 * Only the T4 face so far (layout.face). The drawing holds no state: which page is showing,
 * which key is selected and what each key says all come from the editor.
 */

/** What the drawing shows for one key. */
export interface FaceKey {
  /** Beside the key on the screen, or on the soft key row. Empty for nothing. */
  label: string;
  /** Set by the template, rather than empty or as the phone has it. */
  assigned: boolean;
  /** Its name for a screen reader and the tooltip: "Key 7: Busy lamp, Sipho". */
  describe: string;
}

export const FIXED_KEY_NAMES: Readonly<Record<FixedKey, string>> = {
  soft1: 'Soft key 1', soft2: 'Soft key 2', soft3: 'Soft key 3', soft4: 'Soft key 4',
  up: 'Up', down: 'Down', left: 'Left', right: 'Right', ok: 'OK', cancel: 'Cancel',
  hold: 'Hold', mute: 'Mute', transfer: 'Transfer', redial: 'Redial', message: 'Messages',
};

/** What each fixed key reads as on the phone itself. */
const CAPTION: Readonly<Record<FixedKey, string>> = {
  soft1: '', soft2: '', soft3: '', soft4: '',
  up: '▲', down: '▼', left: '◀', right: '▶', ok: 'OK', cancel: '✕',
  hold: 'Hold', mute: 'Mute', transfer: 'Tran', redial: 'Redial', message: 'Msg',
};

/**
 * How the line keys page. Up to the physical keys, one page holds them all; past them, a page
 * holds one fewer and the bottom right key turns the pages (Yealink V86 administrator guide).
 */
export function linePaging(layout: HandsetLayout, showPages: boolean): { perPage: number; pages: number } {
  const physical = layout.line_keys.per_side * 2;
  return showPages
    ? { perPage: physical - 1, pages: layout.line_keys.pages }
    : { perPage: physical, pages: 1 };
}

export function HandsetFace({
  layout, model, page, showPages, line, fixed, selected, onSelect, onPage,
}: {
  layout: HandsetLayout;
  model: string;
  /** From 1. */
  page: number;
  /** Whether the line keys are paged: some key past the physical ones is in use. */
  showPages: boolean;
  line: (position: number) => FaceKey;
  fixed: (key: FixedKey) => FaceKey;
  /** 'line:7' or 'fixed:12'. */
  selected: string | null;
  onSelect: (slot: string) => void;
  onPage: (page: number) => void;
}) {
  const perSide = layout.line_keys.per_side;
  const { perPage, pages } = linePaging(layout, showPages);
  const first = (page - 1) * perPage + 1;
  const next = (page % pages) + 1;
  const has = (k: FixedKey) => layout.fixed.find((f) => f.key === k);

  // Physical slot i: the left column top to bottom, then the right. With pages, the last one
  // turns them.
  const numberAt = (i: number): number | null => (showPages && i === perSide * 2 - 1 ? null : first + i);

  const lineKey = (i: number) => {
    const n = numberAt(i);
    if (n === null) {
      return (
        <button key="page" type="button" className="arc-phone-key" data-assigned="true"
          aria-label={`Turn to page ${next} of ${pages}`} title={`Page ${next}`} onClick={() => onPage(next)}>
          <span className="arc-phone-led" />
        </button>
      );
    }
    const k = line(n);
    const slot = `line:${n}`;
    return (
      <button key={slot} type="button" className="arc-phone-key"
        data-assigned={k.assigned} data-selected={selected === slot}
        aria-label={k.describe} aria-pressed={selected === slot} title={k.describe}
        onClick={() => onSelect(slot)}>
        <span className="arc-phone-led" />
      </button>
    );
  };

  const cell = (i: number, side: 'left' | 'right') => {
    const n = numberAt(i);
    const text = n === null ? `${page}/${pages}` : line(n).label;
    return (
      <span key={`${side}${i}`} className="arc-phone-cell" data-side={side}
        data-selected={n !== null && selected === `line:${n}`}>
        {text}
      </span>
    );
  };

  const fixedKey = (k: FixedKey, extra?: { wide?: boolean; caption?: string }) => {
    const f = has(k);
    if (!f) return null;
    const face = fixed(k);
    const slot = `fixed:${f.index}`;
    return (
      <button key={slot} type="button" data-key={k}
        className={`arc-phone-key${extra?.wide ? ' arc-phone-wide' : ''}`}
        data-assigned={face.assigned} data-selected={selected === slot}
        aria-label={face.describe} aria-pressed={selected === slot} title={face.describe}
        onClick={() => onSelect(slot)}>
        {extra?.caption ?? CAPTION[k]}
        {face.assigned && <span className="arc-phone-led" />}
      </button>
    );
  };

  /** Drawn where it is on the phone, and not offered. */
  const plain = (text: string, wide = false) => (
    <span key={text} className={`arc-phone-key${wide ? ' arc-phone-wide' : ''}`} aria-hidden="true">{text}</span>
  );

  const rows = Array.from({ length: perSide }, (_, r) => r);
  const soft: FixedKey[] = ['soft1', 'soft2', 'soft3', 'soft4'];
  const twoColumns = has('hold') !== undefined;

  return (
    <div className="arc-phone" role="group" aria-label={`${model} keys`}>
      <div className="arc-phone-handset" aria-hidden="true" />
      <div className="arc-phone-deck">
        <div className="arc-phone-top">
          <div className="arc-phone-col" data-side="left">{rows.map((r) => lineKey(r))}</div>
          <div className="arc-phone-screen" data-colour={layout.screen.colour}>
            <div className="arc-phone-status">
              <span>{model}</span>
              {pages > 1 && <span>Page {page} of {pages}</span>}
            </div>
            <div className="arc-phone-rows">
              {rows.map((r) => [cell(r, 'left'), cell(perSide + r, 'right')])}
            </div>
            <div className="arc-phone-softrow">
              {soft.map((k) => (
                <span key={k} data-selected={selected === `fixed:${has(k)?.index}`}>
                  {has(k) ? fixed(k).label : ''}
                </span>
              ))}
            </div>
          </div>
          <div className="arc-phone-col" data-side="right">{rows.map((r) => lineKey(perSide + r))}</div>
        </div>

        <div className="arc-phone-soft">{soft.map((k) => fixedKey(k))}</div>

        <div className="arc-phone-bottom">
          <div className="arc-phone-pad" aria-hidden="true">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((d) => (
              <span key={d} className="arc-phone-key">{d}</span>
            ))}
          </div>
          <div className="arc-phone-nav">
            <div className="arc-phone-ring">
              {fixedKey('up')}{fixedKey('left')}{fixedKey('ok')}{fixedKey('right')}{fixedKey('down')}
            </div>
            {fixedKey('cancel')}
          </div>
          {twoColumns ? (
            <div className="arc-phone-fn" data-cols="2">
              {plain('Headset')}{fixedKey('mute')}
              {fixedKey('message')}{fixedKey('hold')}
              {fixedKey('redial')}{fixedKey('transfer')}
              {plain('Speaker', true)}
            </div>
          ) : (
            <div className="arc-phone-fn">
              {fixedKey('mute')}{plain('Headset')}{fixedKey('message')}{fixedKey('redial')}
              {plain('Speaker')}
            </div>
          )}
        </div>
        <div className="arc-phone-volume" aria-hidden="true" />
      </div>
    </div>
  );
}
